import { useEffect, useRef, useState } from 'react';
import type { AssetResolver } from '@/engine';

/**
 * 얼굴 PIP 에 **실제로 무엇이 잘려 들어가는지** 보여주는 썸네일.
 *
 * 🔴 왜 있나. 웹캠 위치는 계정에 한 번 등록하면 그 뒤 모든 클립에 그대로 쓰인다.
 * 그런데 그 방송에서 웹캠을 껐거나 OBS 배치를 바꿨으면 **같은 좌표가 엉뚱한 것**
 * (미니맵·체력바)을 가리키고, 그게 네모나게 얹힌 채로 발행된다.
 *
 * 자동 감지를 만들지 않는 이유는 실사용자가 반대했기 때문이다. 원문 —
 * "감지까지 만들지 마라. 웹캠 켜는 사람은 거의 항상 켠다. 클립별로 온오프를 판정하는
 *  로직은 만들 값어치가 없고, **잘못 판정하면 켜져 있는 얼굴을 빼먹는다. 그게 더 나쁘다.**
 *  대신 렌더 돌리기 전에 PIP 에 뭐가 잘려 들어가는지 **썸네일로 보여줘라.** 미니맵 조각이면
 *  사람 눈에 0.5초 만에 보인다. 그 자리에 「얼굴 빼기」가 같이 있으면 끝이다.
 *  … 렌더 다 끝난 뒤에야 알게 되는 구조면 그건 사람한테 떠넘긴 게 맞다. 렌더 전에 보이면
 *  그냥 확인 절차다. **그 차이는 크다.**"
 *
 * 그래서 이 컴포넌트는 판정하지 않는다. **보여주기만 한다.**
 */

export interface WebcamPipPreviewProps {
  resolver: AssetResolver;
  assetId: string;
  /** 소스 화면에서 웹캠으로 잡아 둔 영역(0~1 정규화). */
  crop: { x: number; y: number; w: number; h: number };
  /** 어느 지점을 뽑을지 (소스 초). */
  atSec: number;
  /** 썸네일 가로 px. */
  width?: number;
}

const DEFAULT_WIDTH = 120;
/** 이벤트가 영영 안 올 때를 대비한 상한(ms). 썸네일 하나 때문에 패널이 멈추면 안 된다. */
const EVENT_TIMEOUT_MS = 8000;

/** 이벤트를 한 번 기다린다. 타임아웃이면 거부한다. */
function once(el: HTMLVideoElement, event: 'loadeddata' | 'seeked'): Promise<void> {
  return new Promise((resolve, reject) => {
    const done = (fn: () => void) => () => {
      el.removeEventListener(event, ok);
      el.removeEventListener('error', bad);
      clearTimeout(timer);
      fn();
    };
    const ok = done(resolve);
    const bad = done(() => reject(new Error(`${event} failed`)));
    const timer = setTimeout(bad, EVENT_TIMEOUT_MS);
    el.addEventListener(event, ok, { once: true });
    el.addEventListener('error', bad, { once: true });
  });
}

export function WebcamPipPreview({
  resolver,
  assetId,
  crop,
  atSec,
  width = DEFAULT_WIDTH,
}: WebcamPipPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    let video: HTMLVideoElement | null = null;
    setState('loading');

    void (async () => {
      try {
        const url = await resolver(assetId);
        if (cancelled) return;

        video = document.createElement('video');
        // 캔버스에서 픽셀을 읽어야 하므로 CORS 를 명시한다. 엔진도 같은 호스트를
        // `fetch` 로 받으므로 헤더는 이미 열려 있다.
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        video.src = url;
        await once(video, 'loadeddata');
        if (cancelled) return;

        /*
         * 정확히 0초를 요청하면 이미 그 위치라 `seeked` 가 안 오는 브라우저가 있다.
         * 최소 한 프레임 뒤로 밀고, 소스 끝도 넘지 않게 클램프한다.
         */
        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        const target = Math.min(Math.max(atSec, 1 / 30), Math.max(1 / 30, duration - 0.05));
        video.currentTime = target;
        await once(video, 'seeked');
        if (cancelled) return;

        const vw = video.videoWidth;
        const vh = video.videoHeight;
        const sw = crop.w * vw;
        const sh = crop.h * vh;
        const canvas = canvasRef.current;
        if (!canvas || vw === 0 || vh === 0 || sw < 1 || sh < 1) throw new Error('empty frame');

        canvas.width = width;
        canvas.height = Math.max(1, Math.round((sh / sw) * width));
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('no 2d context');
        // 소스에서 **웹캠 영역만** 잘라 그린다 — 렌더러가 얹는 것과 같은 사각형이다.
        ctx.drawImage(video, crop.x * vw, crop.y * vh, sw, sh, 0, 0, canvas.width, canvas.height);
        setState('ready');
      } catch {
        if (!cancelled) setState('error');
      } finally {
        // 메모리를 붙들지 않게 확실히 끊는다.
        if (video) {
          video.removeAttribute('src');
          video.load();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolver, assetId, crop.x, crop.y, crop.w, crop.h, atSec, width]);

  if (state === 'error') {
    // 썸네일을 못 뽑았다고 **아무 말도 안 하면 안 된다.** "확인했다"고 착각하게 된다.
    return (
      <span className="text-[0.7rem] text-muted-foreground">
        미리보기를 못 불러왔어요 — 내보내기 전에 미리보기 화면에서 확인해 주세요
      </span>
    );
  }

  return (
    <span className="inline-flex flex-col gap-1">
      <canvas
        ref={canvasRef}
        aria-label="얼굴 PIP 에 들어가는 화면"
        style={{ width, minHeight: 24 }}
        className="rounded border bg-muted"
      />
      <span className="text-[0.65rem] text-muted-foreground">
        {state === 'loading' ? '불러오는 중…' : '이게 얹혀요'}
      </span>
    </span>
  );
}
